/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createAsyncThunk } from '@reduxjs/toolkit';

import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import {
  getActionSkillId,
  getAssistantId
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { selectSkillById } from '@ibm-watson/shared/modules/ia/store/skills/core-selectors';
import { triggerSaveJson } from '@ibm-watson/shared/modules/ia/util/download';
import { ApiStatus } from '../../../../../ts';
import { CreateAsyncThunk, DataTypesSlice } from '../../../../../ts/state';
import { selectIsAccessUpdatesEnabled } from '../../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../../ia/store/instance/selectors';
import { listCustomDataTypes } from '../../../api/data-types';
import { listUserEntities } from '../../../api/entities/entities';

export const exportSelectedDataTypes = createAsyncThunk<
  void,
  {
    dataTypeIds: string[];
  },
  CreateAsyncThunk
>('data-types/export', async (params, { dispatch, getState }) => {
  const state = getState();
  const skillId = getActionSkillId(state);
  const { name } = selectSkillById(state, skillId);
  const { dataTypeIds = [] } = params;
  const dataTypeIdSet = new Set(dataTypeIds);

  // Use revised API for Access Control improvements if enabled & isNewIA
  // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on list calls
  const assistantId = getAssistantId(state);
  const isNewIA = selectIsNewIa(state);
  const useNewIAUpdatedAccess = isNewIA && selectIsAccessUpdatesEnabled(state);
  try {
    const { data_types } = await listCustomDataTypes(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      {
        export: true
      }
    );
    const { entities } = await listUserEntities(
      skillId,
      useNewIAUpdatedAccess,
      assistantId,
      {
        page_limit: 1000000
      }
    );
    const selectedDataTypes = data_types.filter(({ data_type }) =>
      dataTypeIdSet.has(data_type)
    );
    const entityReferenceSet = new Set(
      selectedDataTypes.map(({ entity }) => entity)
    );
    const referencedEntities = entities.filter(({ entity }) =>
      entityReferenceSet.has(entity)
    );
    const exportData: any = {
      data_types: selectedDataTypes,
      entities: referencedEntities
    };
    triggerSaveJson(exportData, `${name}-saved_responses.json`);
  } catch (err) {
    dispatch(addErrorMessage(err));
    throw err;
  }
});

const reducer = {
  [exportSelectedDataTypes.pending.toString()]: (state: DataTypesSlice) => {
    state.exportStatus = ApiStatus.LOADING;
    state.exportError = null;
  },
  [exportSelectedDataTypes.fulfilled.toString()]: (state: DataTypesSlice) => {
    state.exportStatus = ApiStatus.SUCCEEDED;
    state.exportError = null;
  },
  [exportSelectedDataTypes.rejected.toString()]: (
    state: DataTypesSlice,
    action: AnyAction
  ) => {
    state.exportStatus = ApiStatus.ERROR;
    state.exportError = action.error;
  }
};

export default reducer;
